<script setup>
import { useStore } from 'vuex';
import { ref, defineProps, defineEmits, computed } from 'vue';
import Pagination from '@/components/LightPagination.vue';
import LoadingTable from '@/components/LoadingTable.vue';
import EquipmentSkeletonTable from '@/components/EquipmentSkeletonTable.vue';
// import MyLoadsDesktopPaginationComponent from '@/components/myLoads/MyLoadsDesktopPaginationComponent.vue';
import MyLoadsDesktopButtonComponent from '@/components/myLoads/MyLoadsDesktopButtonComponent.vue';
import Popper from 'vue3-popper';
import dayjs from 'dayjs';

const props = defineProps({
  data: {
    type: Array,
    required: true,
  },
  pagination: {
    type: Object,
    required: true,
  },
  showDetail: {
    type: Boolean,
    default: false,
  },
  selectedCarrier: { type: [Number, String, null], default: null },
  displaySkeleton: {
    type: Boolean,
    default: false,
  },
  showMoreRows: {
    type: Boolean,
    default: false,
  },
  isModal: {
    type: Boolean,
    default: false,
  },
});

const store = useStore();
const emit = defineEmits(['click', 'resolveIssuesClick']);

const popperStatus = ref({});
const isLoading = ref(false);
const carrierId = computed(
  () =>
    store.state.users.currentUser?.requested_carrier_id ||
    store.state.users.currentUser?.company?.carrier_id
);

const togglePopper = (key, isVisible) => {
  if (!popperStatus.value[key] || !isVisible) {
    popperStatus.value[key] = { isVisible: false, message: 'Copy VIN' };
  }
  popperStatus.value[key].isVisible = isVisible;
};

const copyVinToClipboard = async (vin, key) => {
  try {
    await navigator.clipboard.writeText(vin);
    popperStatus.value[key].message = 'Copied !';
    togglePopper(key, true);
    setTimeout(() => {
      togglePopper(key, false);
    }, 1500);
  } catch (err) {
    console.error('Cant save the VIN on the Clipboard:', err);
  }
};

const onSelectPage = async (page) => {
  isLoading.value = true;
  await store.dispatch('carriers/getCarrierVehiclesForCarrier', {
    carrierId: carrierId.value,
    page,
  });
  isLoading.value = false;
};

// const onClick = (carrier) => {
//   emit('click', carrier);
// };

// table columns
const isVerifiedStatuses = computed(() =>
  props.data.map((vehicle) => vehicle.verified)
);
const truckNumbers = computed(() =>
  props.data.map((vehicle) => vehicle.truck_number)
);
const vins = computed(() => props.data.map((vehicle) => vehicle.vin));
const makes = computed(() => props.data.map((vehicle) => vehicle.make));
const years = computed(() => props.data.map((vehicle) => vehicle.year));
const models = computed(() => props.data.map((vehicle) => vehicle.model));
const isInsuredStatuses = computed(() =>
  props.data.map((vehicle) => vehicle.is_coi)
);
const sources = computed(() => props.data.map((vehicle) => vehicle.source));
const pings = computed(() =>
  props.data.map((vehicle) => dayjs(vehicle.last_seen).format('MM/DD/YYYY'))
);

const isSetEldExemptFeatureEnabled = computed(
  () =>
    store.state.users.featureFlags
      ?.fe_carrier_set_carrier_vehicle_eld_exempt_status?.enabled
);
</script>

<template>
  <LoadingTable
    v-if="isLoading && !displaySkeleton"
    class="relative h-[calc(100vh-505px)] overflow-y-auto rounded-md"
  />
  <EquipmentSkeletonTable v-else-if="displaySkeleton && isLoading" />
  <div class="flex w-full flex-col">
    <!-- table -->
    <div
      class="border-1 scrollbar-thin relative max-h-[calc(100vh-320px)] min-h-[150px] max-w-full overflow-x-scroll overflow-y-scroll rounded-t-[5px] border border-b-0 border-neutral-300-a"
      :class="isModal ? 'h-[calc(100vh-500px)]' : ''"
    >
      <div class="flex flex-1">
        <!-- table columns -->
        <!-- col 1 -->
        <div class="flex flex-1 flex-col">
          <div
            class="sticky top-0 flex h-12 w-full items-center border-b bg-steel-100 py-1.5 pl-2 text-sm font-medium leading-tight tracking-tight text-steel-1100"
          >
            Verified
          </div>
          <!-- @mouseover="hoverIndex = index"
          @mouseleave="hoverIndex = null" -->
          <div
            v-for="(isVerified, index) of isVerifiedStatuses"
            :key="index"
            class="flex h-14 min-w-fit items-center justify-start whitespace-normal break-words border-t border-neutral-300-a py-1.5 pl-2 text-xs font-normal leading-none tracking-tight text-steel-800"
            :class="[
              {
                'pb-1': index !== data?.length - 1,
                // 'bg-steel-200': index === hoverIndex,
                // underline: index === hoverIndex,
                'border-t-0': index === 0,
                'border-b': index === data?.length - 1,
              },
            ]"
          >
            <img
              v-if="isVerified"
              src="@/assets/verified-icon.svg"
              alt="Verified Icon"
            />
            <img
              v-else
              src="@/assets/unverified-icon.svg"
              alt="Unverified Icon"
            />
          </div>
        </div>
        <!-- col 2 -->
        <!-- no wrapping for status pills -->
        <div class="flex min-w-fit flex-1 flex-col">
          <div
            class="sticky top-0 flex h-12 w-full items-center border-b bg-steel-100 py-1.5 text-sm font-medium leading-tight tracking-tight text-steel-1100"
          >
            Truck #
          </div>
          <div
            v-for="(truckNumber, index) of truckNumbers"
            :key="index"
            class="flex h-14 items-center justify-start border-t border-neutral-300-a py-1.5 text-xs font-normal capitalize leading-none tracking-tight text-steel-800"
            :class="[
              {
                'pb-1': index !== data?.length - 1,
                // 'bg-steel-200': index === hoverIndex,
                'border-t-0': index === 0,
                'border-b': index === data?.length - 1,
              },
            ]"
          >
            <div
              class="flex items-center justify-center gap-1 rounded-full py-0.5"
            >
              {{ truckNumber || '-' }}
            </div>
          </div>
        </div>
        <div class="flex flex-1 flex-col">
          <div
            class="sticky top-0 flex h-12 w-full items-center border-b bg-steel-100 py-1.5 pl-3 text-sm font-medium leading-tight tracking-tight text-steel-1100"
          >
            VIN
          </div>
          <div
            v-for="(vin, index) of vins"
            :key="index"
            class="flex h-14 flex-col items-start justify-center gap-0.5 border-t border-neutral-300-a py-1.5 pl-3 text-xs font-normal leading-none tracking-tight text-steel-800"
            :class="[
              {
                'pb-1': index !== data?.length - 1,
                // 'bg-steel-200': index === hoverIndex,
                'border-t-0': index === 0,
                'border-b': index === data?.length - 1,
              },
            ]"
          >
            <div
              class="flex items-center justify-center gap-1 rounded-full py-0.5"
            >
              {{ vin || '-' }}
            </div>
          </div>
        </div>
        <div class="flex flex-1 flex-col">
          <div
            class="sticky top-0 flex h-12 w-full items-center border-b bg-steel-100 py-1.5 text-sm font-medium leading-tight tracking-tight text-steel-1100"
          ></div>
          <div
            v-for="(vin, index) of vins"
            :key="index"
            class="flex h-14 flex-col items-start justify-center gap-0.5 border-t border-neutral-300-a py-1.5 text-xs font-normal leading-none tracking-tight text-steel-800"
            :class="[
              {
                'pb-1': index !== data?.length - 1,
                // 'bg-steel-200': index === hoverIndex,
                'border-t-0': index === 0,
                'border-b': index === data?.length - 1,
              },
            ]"
          >
            <div
              v-if="vin"
              class="mb-0.5"
            >
              <Popper
                :show="popperStatus[index]?.isVisible"
                placement="top"
                :arrow="true"
              >
                <div
                  class="cursor-pointer text-steel-600 hover:text-steel-900 active:opacity-70"
                  @mouseenter="togglePopper(index, true)"
                  @mouseleave="togglePopper(index, false)"
                  @click="copyVinToClipboard(vin, index)"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    class="text-current"
                  >
                    <path
                      d="M13.125 14.375V17.1875C13.125 17.705 12.705 18.125 12.1875 18.125H4.0625C3.81386 18.125 3.5754 18.0262 3.39959 17.8504C3.22377 17.6746 3.125 17.4361 3.125 17.1875V6.5625C3.125 6.045 3.545 5.625 4.0625 5.625H5.625C6.04381 5.62472 6.46192 5.65928 6.875 5.72834M13.125 14.375H15.9375C16.455 14.375 16.875 13.955 16.875 13.4375V9.375C16.875 5.65834 14.1725 2.57417 10.625 1.97834C10.2119 1.90928 9.79381 1.87472 9.375 1.875H7.8125C7.295 1.875 6.875 2.295 6.875 2.8125V5.72834M13.125 14.375H7.8125C7.56386 14.375 7.3254 14.2762 7.14959 14.1004C6.97377 13.9246 6.875 13.6861 6.875 13.4375V5.72834M16.875 11.25V9.6875C16.875 8.94158 16.5787 8.22621 16.0512 7.69876C15.5238 7.17132 14.8084 6.875 14.0625 6.875H12.8125C12.5639 6.875 12.3254 6.77623 12.1496 6.60041C11.9738 6.4246 11.875 6.18614 11.875 5.9375V4.6875C11.875 4.31816 11.8023 3.95243 11.6609 3.6112C11.5196 3.26998 11.3124 2.95993 11.0512 2.69876C10.7901 2.4376 10.48 2.23043 10.1388 2.08909C9.79757 1.94775 9.43184 1.875 9.0625 1.875H8.125"
                      stroke="currentColor"
                      stroke-width="1.25"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </div>
                <template #content>
                  <div
                    class="w-auto whitespace-nowrap rounded-md bg-dark-gray px-2 py-1 text-center text-2xs text-white"
                  >
                    {{ popperStatus[index]?.message }}
                  </div>
                </template>
              </Popper>
            </div>
          </div>
        </div>
        <div class="flex flex-1 flex-col">
          <div
            class="sticky top-0 flex h-12 w-full items-center border-b bg-steel-100 py-1.5 text-sm font-medium leading-tight tracking-tight text-steel-1100"
          >
            Year
          </div>
          <div
            v-for="(year, index) of years"
            :key="index"
            class="flex h-14 flex-col items-start justify-center gap-0.5 border-t border-neutral-300-a py-1.5 text-xs font-normal leading-none tracking-tight text-steel-800"
            :class="[
              {
                'pb-1': index !== data?.length - 1,
                // 'bg-steel-200': index === hoverIndex,
                'border-t-0': index === 0,
                'border-b': index === data.length - 1,
              },
            ]"
          >
            <div
              class="flex items-center justify-center gap-1 rounded-full py-0.5"
            >
              {{ year || '-' }}
            </div>
          </div>
        </div>
        <div class="flex min-w-fit flex-1 flex-col">
          <div
            class="sticky top-0 flex h-12 items-center border-b bg-steel-100 py-1.5 pl-3 text-sm font-medium leading-tight tracking-tight text-steel-1100"
          >
            Make
          </div>
          <div
            v-for="(make, index) of makes"
            :key="index"
            class="flex h-14 flex-shrink-0 items-center justify-start border-t border-neutral-300-a py-1.5 pl-3 text-xs font-normal capitalize leading-none tracking-tight text-steel-800"
            :class="[
              {
                'pb-1': index !== data?.length - 1,
                // 'bg-steel-200': index === hoverIndex,
                'border-t-0': index === 0,
                'border-b': index === data?.length - 1,
              },
            ]"
          >
            <div
              class="flex items-center justify-center gap-1 rounded-full py-0.5"
            >
              {{ make || '-' }}
            </div>
          </div>
        </div>
        <div class="flex flex-1 flex-col">
          <div
            class="sticky top-0 flex h-12 w-full items-center border-b bg-steel-100 py-1.5 pl-3 text-sm font-medium leading-tight tracking-tight text-steel-1100"
          >
            Model
          </div>
          <div
            v-for="(model, index) of models"
            :key="index"
            class="flex h-14 items-center justify-start border-t border-neutral-300-a py-1.5 pl-3 text-xs font-normal leading-none tracking-tight text-steel-800"
            :class="[
              {
                'pb-1': index !== data?.length - 1,
                // 'bg-steel-200': index === hoverIndex,
                'border-t-0': index === 0,
                'border-b': index === data.length - 1,
              },
            ]"
          >
            <div
              class="flex items-center justify-center gap-1 rounded-full py-0.5"
            >
              {{ model || '-' }}
            </div>
          </div>
        </div>
        <div class="flex flex-1 flex-col">
          <div
            class="sticky top-0 flex h-12 w-full items-center border-b bg-steel-100 py-1.5 pl-3 text-sm font-medium leading-tight tracking-tight text-steel-1100"
          >
            COI
          </div>
          <div
            v-for="(isInsured, index) of isInsuredStatuses"
            :key="index"
            class="flex h-14 items-center justify-start gap-1 border-t border-neutral-300-a py-1.5 pl-3 text-xs font-normal leading-none tracking-tight text-steel-800"
            :class="[
              {
                'pb-1': index !== data?.length - 1,
                // 'bg-steel-200': index === hoverIndex,
                'border-t-0': index === 0,
                'border-b': index === data?.length - 1,
              },
            ]"
          >
            <div class="mb-0.5">
              <img
                v-if="isInsured"
                src="@/assets/insured-icon.svg"
                alt="Insured Icon"
              />
              <img
                v-else
                src="@/assets/coi-not-found-icon.svg"
                alt="COI Not Found Icon"
              />
            </div>
            <p class="text-xs">
              {{ isInsured ? 'Insured' : 'Not Found' }}
            </p>
          </div>
        </div>
        <div class="flex flex-1 flex-col">
          <div
            class="sticky top-0 flex h-12 w-full items-center border-b bg-steel-100 py-1.5 pl-3 text-sm font-medium leading-tight tracking-tight text-steel-1100"
          >
            Source
          </div>
          <div
            v-for="(source, index) of sources"
            :key="index"
            class="flex h-14 items-center justify-start border-t border-neutral-300-a py-1.5 pl-3 text-xs font-normal leading-none tracking-tight text-steel-800"
            :class="[
              {
                'pb-1': index !== data?.length - 1,
                // 'bg-steel-200': index === hoverIndex,
                'border-t-0': index === 0,
                'border-b': index === data?.length - 1,
              },
            ]"
          >
            <div
              class="flex items-center justify-center gap-1 rounded-full py-0.5"
            >
              {{ source || '-' }}
            </div>
          </div>
        </div>
        <div class="flex min-w-fit flex-1 flex-col">
          <div
            class="sticky top-0 flex h-12 w-full items-center border-b bg-steel-100 py-1.5 pl-3 text-sm font-medium leading-tight tracking-tight text-steel-1100"
          >
            ELD
          </div>
          <div
            v-for="(vehicle, index) of data"
            :key="index"
            class="flex h-14 items-center justify-start gap-1 border-t border-neutral-300-a py-1.5 pl-3 text-xs font-normal leading-none tracking-tight text-steel-800"
            :class="[
              {
                'pb-1': index !== data?.length - 1,
                // 'bg-steel-200': index === hoverIndex,
                'border-t-0': index === 0,
                'border-b': index === data?.length - 1,
              },
            ]"
          >
            <div class="mb-1.5 flex-shrink-0">
              <img
                v-if="vehicle.is_eld_and_active"
                src="@/assets/eld-connected-icon.svg"
                alt="ELD Connected Icon"
              />
              <svg
                v-else-if="vehicle.is_eld_exempt"
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
              >
                <path
                  d="M2.01389 8.51314C3.99541 6.35265 6.8391 5.00004 9.99993 5.00004C13.1608 5.00004 16.0044 6.35265 17.986 8.51314C18.297 8.85232 18.8242 8.8751 19.1634 8.56402C19.5026 8.25293 19.5253 7.72579 19.2143 7.38661C16.9303 4.8964 13.6471 3.33337 9.99993 3.33337C6.3528 3.33337 3.06952 4.8964 0.7856 7.38661C0.474516 7.72579 0.497294 8.25293 0.836476 8.56402C1.17566 8.8751 1.7028 8.85232 2.01389 8.51314Z"
                  fill="#1F242E"
                  fill-opacity="0.48"
                />
                <path
                  d="M10 17.5C10.9205 17.5 11.6667 16.7538 11.6667 15.8334C11.6667 14.9129 10.9205 14.1667 10 14.1667C9.07953 14.1667 8.33333 14.9129 8.33333 15.8334C8.33333 16.7538 9.07953 17.5 10 17.5Z"
                  fill="#1F242E"
                  fill-opacity="0.48"
                />
                <path
                  d="M9.9999 8.33337C8.01025 8.33337 6.20312 9.10703 4.85997 10.3716C4.52487 10.687 3.99747 10.6711 3.68199 10.336C3.36651 10.0009 3.38241 9.47354 3.71751 9.15806C5.35753 7.61405 7.56899 6.66671 9.9999 6.66671C12.4308 6.66671 14.6423 7.61405 16.2823 9.15806C16.6174 9.47354 16.6333 10.0009 16.3178 10.336C16.0023 10.6711 15.4749 10.687 15.1398 10.3716C13.7967 9.10703 11.9895 8.33337 9.9999 8.33337Z"
                  fill="#1F242E"
                  fill-opacity="0.48"
                />
                <path
                  d="M7.29776 12.6615C8.02569 12.0407 8.96802 11.6667 9.99983 11.6667C11.0316 11.6667 11.974 12.0407 12.7019 12.6615C13.0521 12.9602 13.578 12.9184 13.8767 12.5682C14.1754 12.218 14.1336 11.6921 13.7834 11.3934C12.7652 10.525 11.4428 10 9.99983 10C8.55688 10 7.23441 10.525 6.21624 11.3934C5.86606 11.6921 5.8243 12.218 6.12295 12.5682C6.42161 12.9184 6.94759 12.9602 7.29776 12.6615Z"
                  fill="#1F242E"
                  fill-opacity="0.48"
                />
              </svg>
              <img
                v-else
                src="@/assets/eld-not-connected-icon.svg"
                alt="ELD Not Connected"
              />
            </div>
            <p class="text-xs">
              {{
                vehicle.is_eld_and_active
                  ? 'Connected'
                  : vehicle.is_eld_exempt
                    ? 'ELD Exempt'
                    : 'Not Connected'
              }}
            </p>
          </div>
        </div>
        <div class="flex flex-1 flex-col">
          <div
            class="sticky top-0 flex h-12 w-full items-center border-b bg-steel-100 py-1.5 pl-3 text-sm font-medium leading-tight tracking-tight text-steel-1100"
          >
            Last Seen
          </div>
          <div
            v-for="(ping, index) of pings"
            :key="index"
            class="flex h-14 items-center justify-start border-t border-neutral-300-a py-1.5 pl-3 text-xs font-normal leading-none tracking-tight text-steel-800"
            :class="[
              {
                'pb-1': index !== data?.length - 1,
                // 'bg-steel-200': index === hoverIndex,
                'border-t-0': index === 0,
                'border-b': index === data?.length - 1,
              },
            ]"
          >
            <div
              class="flex items-center justify-center gap-1 rounded-full py-0.5"
            >
              {{ ping || '-' }}
            </div>
          </div>
        </div>
        <div
          v-if="isSetEldExemptFeatureEnabled && !isModal"
          class="flex flex-1 flex-col"
        >
          <div
            class="sticky top-0 flex h-12 w-full items-center border-b bg-steel-100 py-1.5 pl-3 pr-2 text-sm font-medium leading-tight tracking-tight text-steel-1100"
          ></div>
          <div
            v-for="(vehicle, index) of data"
            :key="index"
            class="flex h-14 items-center justify-start border-t border-neutral-300-a py-1.5 pl-3 pr-2 text-xs font-normal leading-none tracking-tight text-steel-800"
            :class="[
              {
                'pb-1': index !== data?.length - 1,
                // 'bg-steel-200': index === hoverIndex,
                'border-t-0': index === 0,
                'border-b': index === data?.length - 1,
              },
            ]"
          >
            <div
              v-if="vehicle.eld_exempt_category?.includes('pending')"
              class="flex items-center justify-center gap-1 whitespace-nowrap rounded-[2px] bg-steel-200 p-1"
            >
              Exemption Pending
            </div>
            <div
              v-else-if="vehicle.eld_exempt_category?.includes('denied')"
              class="flex items-center justify-center gap-1 whitespace-nowrap rounded-[2px] bg-rose-200 p-1"
            >
              Exemption Denied
            </div>
            <div
              v-else-if="vehicle.eld_exempt_category"
              class="flex items-center justify-center gap-1 whitespace-nowrap rounded-[2px] bg-forest-200 p-1"
            >
              Exemption Verified
            </div>
            <MyLoadsDesktopButtonComponent
              v-else-if="!vehicle.is_eld_and_active && !vehicle.is_eld_exempt"
              button-class="default-button rounded-[5px] px-3 py-1 h-8"
              text-class="font-medium text-sm leading-loose tracking-tight whitespace-nowrap"
              button-text="Resolve Issues"
              @button-click="
                (e) => {
                  e.stopPropagation();
                  emit('resolveIssuesClick', vehicle);
                }
              "
            />
          </div>
        </div>
      </div>
    </div>
    <div
      class="border-1 flex h-14 max-w-full items-center justify-between rounded-b-[5px] border border-t-0 border-neutral-300-a bg-white px-3 shadow-[0px_-1px_6px_0px_rgba(160,160,160,0.22)]"
    >
      <div
        class="text-xs font-medium leading-none tracking-tight text-steel-700"
      >
        Showing
        {{ data?.length > 0 ? (pagination.currentPage - 1) * 50 + 1 : 0 }} -
        {{ (pagination.currentPage - 1) * 50 + data?.length }} of
        {{ pagination.totalRecords }}
      </div>
      <!-- <MyLoadsDesktopPaginationComponent
        :current-page="pagination.currentPage"
        :total-pages="pagination.totalPages"
        :push-page-param-to-url="true"
        :alignment="'center'"
      /> -->
      <pagination
        :selected-page="pagination.currentPage"
        :total-pages="pagination.totalPages"
        equivalent
        @select-page="onSelectPage"
      ></pagination>
      <div
        class="invisible text-xs font-medium leading-none tracking-tight text-steel-700"
      >
        50 results per page
      </div>
    </div>
  </div>
</template>

<style scoped>
.topShadow {
  box-shadow: 0px -1px 6px 0px rgba(160, 160, 160, 0.22);
}
</style>
